import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDetailedReport } from "../Redux/ActionCreators/reportAction";

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const StageIcon = ({ stroke = "#cbd5e1" }) => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 11 12 14 22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

const AssessmentStage = ({
  candidate,
  currentStage,
  currentStatus,
  assessmentInviteDetails,
  latestJob,
  tabLoading,
  isSharedReport,
  formatDateOnly,
  formatDateTime,
  navigate,
  setShowVideoModal,
  setModalType,
  assessmentStatus,
  initiatingVideoCall,
  useSharedToken,
}) => {
  const dispatch = useDispatch();
  const detailReport = useSelector((s) => s.myAssessment?.detailReport);
  const [reportLoading, setReportLoading] = useState(false);
  const prevInviteId = useRef(null);

  // Synchronously mark as loading before paint so there is no flash of
  // incomplete content between tabLoading clearing and the fetch starting.
  useLayoutEffect(() => {
    if (
      currentStatus === "COMPLETED" &&
      assessmentInviteDetails?.inviteId &&
      prevInviteId.current !== assessmentInviteDetails.inviteId
    ) {
      setReportLoading(true);
    }
  }, [currentStatus, assessmentInviteDetails?.inviteId]);

  useEffect(() => {
    if (
      currentStatus === "COMPLETED" &&
      assessmentInviteDetails?.inviteId &&
      prevInviteId.current !== assessmentInviteDetails.inviteId
    ) {
      prevInviteId.current = assessmentInviteDetails.inviteId;
      dispatch(getDetailedReport(assessmentInviteDetails.inviteId))
        .finally(() => setReportLoading(false));
    }
  }, [currentStatus, assessmentInviteDetails?.inviteId, dispatch]);

  if (tabLoading || reportLoading) {
    return (
      <div className="cpv2-loading" style={{ minHeight: 200 }}>
        <div className="spinner-border" />
      </div>
    );
  }

  const getScoreConfig = (score) => {
    if (score === null || score === undefined) return { label: "N/A", bg: "#f1f5f9", color: "#64748b" };
    if (score < 50)  return { label: "Weak",      bg: "#fee2e2", color: "#991b1b" };
    if (score < 75)  return { label: "Average",   bg: "#fef3c7", color: "#92400e" };
    if (score < 90)  return { label: "Good",      bg: "#dbeafe", color: "#1d4ed8" };
    return             { label: "Excellent", bg: "#dcfce7", color: "#166534" };
  };

  const getBarColor = (pct) => {
    if (pct >= 85) return "#22c55e";
    if (pct >= 60) return "#f59e0b";
    return "#ef4444";
  };

  const sendAssessmentBtn = !isSharedReport && (
    <button
      className="cpv2-assessment__assign-btn"
      onClick={() => { setShowVideoModal(true); setModalType("assessment"); }}
      disabled={initiatingVideoCall}
    >
      {assessmentStatus === "sending"
        ? "Assessment sending to candidate…"
        : assessmentStatus === "sent"
        ? "Assessment sent to candidate"
        : "Send an assessment invite"}
    </button>
  );

  if (!currentStage) {
    return (
      <div className="cpv2-assessment">
        {!latestJob?.assessment_id ? (
          <div className="cpv2-video__status-card cpv2-video__status-card--info" style={{ flexWrap: "wrap", gap: 12 }}>
            <span className="cpv2-video__status-dot cpv2-video__status-dot--blue" />
            <span style={{ flex: 1, fontSize: 13, color: "#525b66" }}>
              Assessment was not added. Add it by navigating to My Assessments.
            </span>
            {!isSharedReport && (
              <button className="cpv2-assessment__assign-btn" style={{ flexShrink: 0 }} onClick={() => navigate("/assessments")}>
                My Assessments
              </button>
            )}
          </div>
        ) : (
          <div className="cpv2-stage-empty">
            <StageIcon />
            <p className="cpv2-stage-empty__msg">No assessment assigned yet</p>
            {sendAssessmentBtn}
          </div>
        )}
      </div>
    );
  }

  if (currentStatus === "ASSIGNED") {
    return (
      <div className="cpv2-assessment">
        <div className="cpv2-video__status-card cpv2-video__status-card--info">
          <span className="cpv2-video__status-dot cpv2-video__status-dot--blue" />
          Assessment assigned — waiting for candidate to complete
        </div>
      </div>
    );
  }

  if (currentStatus === "NOT SCHEDULED" || currentStatus === "SCHEDULED") {
    return (
      <div className="cpv2-assessment">
        <div className="cpv2-video__status-card cpv2-video__status-card--info">
          <span className="cpv2-video__status-dot cpv2-video__status-dot--yellow" />
          Assessment invite sent
          {currentStage?.expire_at && (
            <> — due <strong>{formatDateOnly(currentStage.expire_at)}</strong></>
          )}
        </div>
      </div>
    );
  }

  if (currentStatus === "ONGOING") {
    return (
      <div className="cpv2-assessment">
        <div className="cpv2-video__status-card cpv2-video__status-card--info">
          <span className="cpv2-video__status-dot cpv2-video__status-dot--green cpv2-video__status-dot--pulse" />
          Assessment in progress…
        </div>
      </div>
    );
  }

  if (["EXPIRED", "FAILED", "NOT COMPLETED", "REVOKED"].includes(currentStatus)) {
    return (
      <div className="cpv2-assessment">
        <div className="cpv2-stage-empty">
          {currentStatus === "EXPIRED" ? <StageIcon stroke="#fcd34d" /> : <StageIcon stroke="#fca5a5" />}
          <p className={`cpv2-stage-empty__msg ${currentStatus === "EXPIRED" ? "cpv2-stage-empty__msg--warning" : "cpv2-stage-empty__msg--error"}`}>
            {currentStatus === "EXPIRED" ? "Assessment stage expired." : "Assessment not completed."}
          </p>
          {sendAssessmentBtn}
        </div>
      </div>
    );
  }

  if (currentStatus === "COMPLETED" && assessmentInviteDetails) {
    const score = detailReport?.score ?? assessmentInviteDetails?.score ?? null;
    const testName = detailReport?.testName || assessmentInviteDetails?.testName || "Assessment";
    const duration = detailReport?.candidateTestDuration;
    const totalQ = detailReport?.tests?.[0]?.totalQuestions ?? assessmentInviteDetails?.totalQuestions;
    const topics = detailReport?.tests?.[0]?.topics || [];
    const { label: scoreLabel, bg: scoreBg, color: scoreColor } = getScoreConfig(score);
    const strengths = detailReport?.strengths || [];
    const areasToProbe = detailReport?.areas_to_probe || detailReport?.areasToProbe || [];

    return (
      <div className="cpv2-assessment">

        {/* ── Score header ── */}
        <div className="cpv2-video__score-row" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: 72, height: 72, borderRadius: 12, background: scoreBg, flexShrink: 0 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>
              {score !== null ? Math.round(score) : "--"}
            </span>
            <span style={{ fontSize: 10, fontWeight: 500, color: scoreColor, marginTop: 2 }}>/100</span>
          </div>
          <div className="cpv2-video__score-info">
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
              <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 700, background: scoreBg, color: scoreColor }}>
                {scoreLabel}
              </span>
              <button
                className="cpv2-assessment__view-btn"
                onClick={() => navigate("/test-report", { state: { id: assessmentInviteDetails.inviteId, type: "new" } })}
              >
                <ExternalLinkIcon />
                View Full Report
              </button>
            </div>
            <p style={{ fontSize: 12, color: "#8a9199", margin: 0 }}>
              {testName}
              {(duration || totalQ) && (
                <>
                  &nbsp;·&nbsp;
                  {duration && <>{Math.round(duration / 60)} min</>}
                  {duration && totalQ && " · "}
                  {totalQ && <>{totalQ} questions</>}
                </>
              )}
            </p>
          </div>
        </div>

        {/* ── Topic bars ── */}
        {topics.length > 0 && (
          <div className="cpv2-video__section">
            <h6 className="cpv2-video__section-title">Topic Breakdown</h6>
            <div className="cpv2-assessment__topics">
              {topics.map((topic, idx) => {
                const pct = Math.min(Math.round(topic.score || 0), 100);
                const hasFraction = topic.correctCount != null || topic.correct != null;
                const fraction = hasFraction
                  ? `${topic.correctCount ?? topic.correct}/${topic.totalQuestions ?? topic.total ?? topic.questionCount ?? "--"}`
                  : null;
                const barColor = getBarColor(pct);

                return (
                  <div className="cpv2-assessment__topic-row" key={idx}>
                    <span className="cpv2-assessment__topic-name" title={topic.skillTopicName}>
                      {topic.skillTopicName}
                    </span>
                    <div className="cpv2-assessment__bar-track">
                      <div
                        className="cpv2-assessment__bar-fill"
                        style={{ width: `${pct}%`, background: barColor }}
                      />
                    </div>
                    {fraction && (
                      <span className="cpv2-assessment__topic-fraction">{fraction}</span>
                    )}
                    <span style={{ fontSize: 12, fontWeight: 700, minWidth: 36, textAlign: "right", color: barColor }}>
                      {pct}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Strengths & Areas to probe ── */}
        {(strengths.length > 0 || areasToProbe.length > 0) && (
          <div className="cpv2-video__insights-grid">
            {strengths.length > 0 && (
              <div className="cpv2-video__insight-card cpv2-video__insight-card--green">
                <h6 className="cpv2-video__insight-title">Strengths</h6>
                <ul className="cpv2-video__insight-list">
                  {strengths.map((item, i) => (
                    <li key={i}>
                      <CheckIcon />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {areasToProbe.length > 0 && (
              <div className="cpv2-video__insight-card cpv2-video__insight-card--amber">
                <h6 className="cpv2-video__insight-title">Areas to probe</h6>
                <ul className="cpv2-video__insight-list">
                  {areasToProbe.map((item, i) => (
                    <li key={i}>
                      <AlertIcon />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="cpv2-assessment">
      <div className="cpv2-video__status-card cpv2-video__status-card--info">
        <span className="cpv2-video__status-dot cpv2-video__status-dot--blue" />
        Status pending or unknown.
      </div>
    </div>
  );
};

export default AssessmentStage;
